'use client'

import { motion } from 'framer-motion'
import { Container } from '@/components/container'
import { SectionHeader } from '@/components/section-header'
import { Button } from '@/components/button'
import { trackEvent } from '@/lib/utils'
import { useEffect, useRef } from 'react'
import { 
  CodeBracketIcon, 
  CommandLineIcon, 
  LightBulbIcon,
  RocketLaunchIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline'

const services = [
  {
    title: 'MVP Build & Deployment',
    summary: 'End-to-end MVP development with frontend, backend, and AI integration. From concept to launch in 8-12 weeks.',
    icon: <RocketLaunchIcon className="h-6 w-6" />,
    gradient: 'blue' as const,
    deliverables: 'Full-stack MVP, AI integration, deployment, documentation',
    audience: 'Startups, Entrepreneurs',
  },
  {
    title: 'SaaS Development & Architecture',
    summary: 'Scalable SaaS product architecture, multi-tenant systems, and enterprise-grade infrastructure consulting.',
    icon: <CodeBracketIcon className="h-6 w-6" />,
    gradient: 'purple' as const,
    deliverables: 'Architecture design, technical roadmap, scalable infrastructure',
    audience: 'Startups, Enterprises, SMBs',
  },
  {
    title: 'Code Review & Technical Audit',
    summary: 'Comprehensive code reviews, performance optimization, security audits, and technical debt assessment.',
    icon: <CommandLineIcon className="h-6 w-6" />,
    gradient: 'green' as const,
    deliverables: 'Audit report, optimization recommendations, refactoring plan',
    audience: 'Startups, Enterprises, SMBs',
  },
  {
    title: 'Technical Leadership & Team Augmentation',
    summary: 'On-demand tech leadership, team augmentation, mentoring, and architecture guidance for your development teams.',
    icon: <LightBulbIcon className="h-6 w-6" />,
    gradient: 'orange' as const,
    deliverables: 'Technical leadership, team mentoring, architecture guidance',
    audience: 'Startups, Enterprises',
  },
  {
    title: 'Product Strategy & Roadmap Planning',
    summary: 'Strategic product planning, roadmap development, and product-market fit analysis to guide your product journey.',
    icon: <ChartBarIcon className="h-6 w-6" />,
    gradient: 'purple' as const,
    deliverables: 'Product strategy, roadmap, feature prioritization',
    audience: 'Startups, Enterprises',
  },
]

const gradients = {
  blue: 'from-blue-500/10 via-blue-50 to-white',
  purple: 'from-purple-500/10 via-purple-50 to-white',
  green: 'from-green-500/10 via-green-50 to-white',
  orange: 'from-orange-500/10 via-orange-50 to-white',
}

const iconGradients = {
  blue: 'bg-gradient-to-br from-blue-500 to-blue-600',
  purple: 'bg-gradient-to-br from-purple-500 to-purple-600',
  green: 'bg-gradient-to-br from-green-500 to-green-600',
  orange: 'bg-gradient-to-br from-orange-500 to-orange-600',
}

export function ServicesPreview() {
  const sectionRef = useRef<HTMLElement>(null)
  const hasTrackedView = useRef(false)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasTrackedView.current) {
            hasTrackedView.current = true
            trackEvent('section_viewed', {
              event_category: 'engagement',
              event_label: 'services_preview',
              value: 1,
            })
          }
        })
      },
      { threshold: 0.5 }
    )

    if (sectionRef.current) {
      observer.observe(sectionRef.current)
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current)
      }
    }
  }, [])

  return (
    <section ref={sectionRef} id="services-preview" className="py-24 bg-gradient-to-b from-white to-gray-50">
      <Container>
        <SectionHeader
          subheading="Services"
          heading="What We Offer"
          description="Comprehensive solutions to transform your ideas into scalable, production-ready products."
        />

        <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent my-12" />

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => {
            const gradientClasses = gradients[service.gradient]
            const iconGradient = iconGradients[service.gradient]

            return (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -4 }}
              >
                <div className={`group relative rounded-xl border border-gray-200 bg-gradient-to-br ${gradientClasses} p-6 shadow-sm transition-all hover:shadow-lg hover:border-gray-300 h-full flex flex-col`}>
                  {/* Decorative background element */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-white/20 to-transparent rounded-full blur-2xl -z-0" />
                  
                  <div className="relative z-10">
                    {/* Icon */}
                    <div className={`flex h-12 w-12 items-center justify-center rounded-lg ${iconGradient} text-white shadow-md mb-4 group-hover:scale-110 transition-transform`}>
                      {service.icon}
                    </div>
                    
                    {/* Title */}
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {service.title}
                    </h3>
                    
                    {/* Summary */}
                    <p className="text-sm text-gray-600 mb-3 flex-grow">
                      {service.summary}
                    </p>
                    
                    {/* Deliverables */}
                    {service.deliverables && (
                      <div className="mb-3">
                        <p className="text-xs font-medium text-gray-700 mb-1">Deliverables:</p>
                        <p className="text-xs text-gray-600">{service.deliverables}</p>
                      </div>
                    )}
                    
                    {/* Audience */}
                    {service.audience && (
                      <div className="mb-4">
                        <p className="text-xs font-medium text-gray-700 mb-1">For:</p>
                        <p className="text-xs text-gray-600">{service.audience}</p>
                      </div>
                    )}
                    
                    {/* CTA Button */}
                    <Button
                      href="/services"
                      variant="outline"
                      className="w-full text-sm"
                      onClick={() => {
                        if (typeof window !== 'undefined' && window.gtag) {
                          window.gtag('event', 'service_learn_more_clicked', {
                            event_category: 'engagement',
                            event_label: service.title,
                            value: 1,
                          })
                        }
                      }}
                    >
                      Learn More
                    </Button>
                  </div>
                </div>
              </motion.div>
            )
          })}
        </div>

        <div className="mt-12 text-center">
          <Button 
            href="/services" 
            variant="outline"
            onClick={() => {
              if (typeof window !== 'undefined' && window.gtag) {
                window.gtag('event', 'cta_clicked', {
                  event_category: 'engagement',
                  event_label: 'view_all_services',
                  value: 1,
                })
              }
            }}
          >
            View All Services
          </Button>
        </div>
      </Container>
    </section>
  )
}

